import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowUpRight, HelpCircle, Mail, MessageSquare } from "lucide-react";

const FaqConsumer = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "FAQ – Consumer — FOZZIL";
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const appleEase = [0.16, 1, 0.3, 1];

  const consumerFaqs = [
    {
      q: "What is T.A.E.L.S?",
      a: "T.A.E.L.S stands for Truly Adaptive, Efficient & Learning Synthesis. It is FOZZIL's proprietary cognitive intelligence architecture designed for high-consequence reasoning, deep research, natural voice conversation, and everyday personal productivity."
    },
    {
      q: "How do I create and manage a FOZZIL ID?",
      a: "You can create a FOZZIL ID by clicking 'Sign In' at the top of any page or visiting account.fozzil.in. Your FOZZIL ID securely unifies your authentication across all FOZZIL apps, web dashboards, and ecosystem hardware."
    },
    {
      q: "Can I use T.A.E.L.S for free?",
      a: "Yes. T.A.E.L.S Pulse is free to use for everyday conversational queries, web search synthesis, and hands-free voice assistance. Higher-tier thinking models and zero-telemetry Blacksite environments are available through our subscription plans."
    },
    {
      q: "How does FOZZIL protect my personal data and privacy?",
      a: "FOZZIL operates under strict zero-knowledge principles. Your prompts, uploaded personal files, voice interactions, and browsing sessions are never sold to data brokers or used to train public foundation models without explicit consent."
    },
    {
      q: "What devices and platforms are supported?",
      a: "T.A.E.L.S runs on all modern desktop and mobile web browsers (Safari, Chrome, Edge, Firefox), with full touch optimization, voice interactivity, and responsive multi-device synchronization."
    },
    {
      q: "What is the difference between T.A.E.L.S Pulse and Blacksite?",
      a: "Pulse is optimized for high-speed conversational responses, voice fluidity, and quick research. Blacksite operates inside client-side encrypted enclaves with deep multi-step deliberation, zero telemetry, and permanent cryptographic memory purging."
    },
    {
      q: "How do subscriptions, renewals, and cancellations work?",
      a: "All subscription plans are billed on a recurring monthly or annual basis. You can upgrade, downgrade, or cancel anytime from your Account settings with zero cancellation penalties."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-blue-600/30 selection:text-white font-sans antialiased overflow-x-hidden pt-28 md:pt-36 pb-24 px-6">
      {/* Subtle Atmospheric FOZZIL Ambient Glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-blue-600/[0.04] blur-[160px] rounded-full" />
        <div className="absolute bottom-1/3 left-1/4 w-[500px] h-[500px] bg-sky-500/[0.03] blur-[140px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto space-y-16">
        {/* Answers & Specifications Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="space-y-4 text-center max-w-2xl mx-auto"
        >
          <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">
            Answers & Specifications
          </span>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
            Consumer Edition · Everything you need to know about T.A.E.L.S, FOZZIL ID, and personal intelligence.
          </p>
        </motion.div>

        {/* Accordion Cards */}
        <div className="space-y-4">
          {consumerFaqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: idx * 0.05, ease: appleEase }}
                className="rounded-[24px] bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-all duration-300 hover:border-white/[0.12]"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between p-6 sm:p-7 text-left text-white font-medium text-base sm:text-lg cursor-pointer hover:bg-white/[0.02] transition-colors"
                >
                  <span className="pr-4">{faq.q}</span>
                  <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center text-zinc-400 shrink-0">
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-7 pb-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed border-t border-white/[0.04] pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Support CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: appleEase }}
          className="p-8 sm:p-10 rounded-[32px] bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.08] text-center space-y-5"
        >
          <div className="w-12 h-12 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
            <HelpCircle className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl sm:text-2xl font-semibold text-white">Still have questions?</h3>
            <p className="text-sm sm:text-base text-zinc-400 font-light max-w-md mx-auto">
              Our consumer support team is available around the clock to assist with account access, billing, and product guidance.
            </p>
          </div>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:support@fozzil.in"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-all cursor-pointer shadow-lg"
            >
              <Mail className="w-4 h-4" />
              <span>Contact Support</span>
            </a>
            <a
              href="https://support.fozzil.in/help-center"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.05] hover:bg-white/[0.1] text-white border border-white/[0.1] font-medium text-sm transition-all cursor-pointer"
            >
              <span>Visit Help Centre</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FaqConsumer;